<template>
  <div class="view-container">
    <mobialis-form-with-validation
      @onValidate="submit"
      novalidate
      v-slot="{ errors }"
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <div class="inner-content">
        <h2 class="mt-sm-5 text-center mb-5">{{ $t('message.credit.selectVehicleType.heading') }}</h2>
        <div class="row form-row mb-3" v-if="displayVehicleCategoryTypeOptions">
          <div class="col-sm-6" v-for="(option, index) in radioButtonOptions" :key="index">
            <MobialisFancyRadio
              rules="required"
              :textArray="[option]"
              vid="vehicleCategoryType"
              name="radioButtonOptions"
              v-model="vehicleCategoryType"
              :data-cy="option.value"
            ></MobialisFancyRadio>
          </div>
          <p
            class="text-danger ml-2 small my-0"
            v-if="errors.vehicleCategoryType && errors.vehicleCategoryType.length > 0 && !vehicleCategoryType"
          >
            {{ $t('message.common.pleaseSelect') }}
          </p>
        </div>
        <div v-if="!displayVehicleCategoryTypeOptions || vehicleCategoryType === vehicleCategoryTypes.RECREATIONAL">
          <auto-complete
            v-if="vehicleTypeOptions"
            v-model="vehicleType"
            :sourceData="vehicleTypeOptions"
            :label="$t('message.credit.selectVehicleType.vehicleType')"
            :placeholder="$t('message.credit.selectVehicleType.startTyping')"
          >
          </auto-complete>
        </div>
      </div>
      <div class="submit-button-container my-4 pt-5">
        <mobialis-button type="submit" data-cy="submit">{{ $t('message.common.continue') }}</mobialis-button>
      </div>
    </mobialis-form-with-validation>
  </div>
</template>

<script>
import cloneDeep from 'lodash/fp/cloneDeep';
import { mapGetters } from 'vuex';
import AutoComplete from '@/components/shared/AutoComplete.vue';
import { VehicleCategories, VehicleCategoryTypes } from '@/utils/enums';
import { prequalFinanceProviderMap } from '@/utils/prequalFinanceProviderMap';

export default {
  name: 'SelectVehicleType',
  components: { AutoComplete },
  data() {
    return {
      vehicleCategoryTypes: VehicleCategoryTypes,
      vehicleCategories: VehicleCategories,
      prequalFinanceProviderMap,
      vehicleType: cloneDeep(this.$store.state.vehicle.vehicleType),
      vehicleCategory: cloneDeep(this.$store.state.vehicle.vehicleCategory),
      vehicleCategoryType: cloneDeep(this.$store.state.vehicle.vehicleCategoryType),
      radioButtonOptions: [
        {
          label: this.$t('message.credit.selectVehicleType.automotive'),
          value: VehicleCategoryTypes.AUTO,
        },
        {
          label: this.$t('message.credit.selectVehicleType.recreational'),
          value: VehicleCategoryTypes.RECREATIONAL,
        },
      ],
    };
  },
  computed: {
    ...mapGetters('business', ['prequalificationFinanceProviders']),
    displayVehicleCategoryTypeOptions() {
      return (
        this.vehicleCategoryTypesEnabled.includes(VehicleCategoryTypes.AUTO) &&
        this.vehicleCategoryTypesEnabled.includes(VehicleCategoryTypes.RECREATIONAL)
      );
    },
    vehicleCategoryTypesEnabled() {
      return [
        ...new Set(
          this.prequalificationFinanceProviders.map(
            provider => this.prequalFinanceProviderMap.find(item => item.providerCode === provider).categoryType
          )
        ),
      ];
    },
    vehicleCategoriesEnabled() {
      return this.prequalificationFinanceProviders
        .map(provider => this.prequalFinanceProviderMap.find(item => item.providerCode === provider).category)
        .filter(Boolean);
    },
    vehicleTypeOptions() {
      const filtered = this.vehicleTypes.filter(item => this.vehicleCategoriesEnabled.includes(item.category));
      return filtered.map(v => v.value).sort();
    },
    vehicleTypes() {
      const res = [];
      const vehicleTypes = [
        this.vehicleCategories.RV,
        this.vehicleCategories.POWERSPORTS,
        this.vehicleCategories.MARINE,
      ];
      vehicleTypes.forEach(type => {
        this.$t(`message.credit.selectVehicleType.vehicleTypes.${type}`).forEach(value => {
          res.push({ value, category: type });
        });
      });
      return res;
    },
  },
  methods: {
    submit(isValid) {
      if (!isValid) {
        return;
      }
      this.$store.commit('vehicle/SET_STATE', ['vehicleCategoryType', this.vehicleCategoryType]);
      if (this.vehicleCategoryType === VehicleCategoryTypes.RECREATIONAL || !this.displayVehicleCategoryTypeOptions) {
        this.$store.commit('vehicle/SET_STATE', ['vehicleType', this.vehicleType]);
        this.$store.commit('vehicle/SET_STATE', ['vehicleCategory', this.getVehicleCategory()]);
      } else if (this.vehicleCategoryType === VehicleCategoryTypes.AUTO) {
        this.$store.commit('vehicle/SET_STATE', ['vehicleType', null]);
        this.$store.commit('vehicle/SET_STATE', ['vehicleCategory', VehicleCategories.AUTO]);
      }
      if (this.baseRoute === 'mobile') {
        this.$router.push({
          name: 'credit-disclaimer-mobile',
        });
      } else {
        this.$router.push({
          name: `creditform1-${this.baseRoute}`,
        });
      }
    },
    getVehicleCategory() {
      return this.vehicleTypes.filter(item => item.value === this.vehicleType)[0].category;
    },
  },
};
</script>

<style lang="scss" scoped>
.inner-content {
  max-width: 590px;
  margin: 0 auto;
}
@media only screen and (min-width: $breakpoint-mobile) {
  .view-container {
    min-height: calc(100vh - 280px);
    padding-bottom: 112px;
    position: relative;
    margin-bottom: 40px;
    .submit-button-container {
      width: 100%;
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }
}
</style>
